import { useGameStore } from "../../app/gameStore";
import { CARDS } from "@reagent/content";
import { playCue } from "../../audio/AudioEngine";
import { LiveBuildTray } from "./LiveBuildTray";
import { boundCardRecipeLabel } from "./liveBuild";
import { InstallRecipePicker } from "./RecipePicker";
import "./setup-screen.css";

/**
 * Phase 1: Workshop Setup (`00-pillars.md` §3, spec 0017). Frozen time, before
 * turn 1 — the rig is bought with Credits and placed on the board, rather than
 * dealt as cards and paid for out of a turn's kW.
 *
 * This is a docked column, not a modal, and that is load-bearing rather than
 * cosmetic: buying and placing are one gesture split across two surfaces, so
 * the catalogue has to be readable while the board stays clickable. See the
 * note at the top of setup-screen.css.
 *
 * Every stage shipped today opens with an empty catalogue and a zero budget,
 * so `enterPlay()` in the store skips straight past this screen — there is
 * nothing to decide. It only renders once a stage actually ships a catalogue,
 * so an empty list and a 0/0 budget below are the sane resting state for that
 * case, not a bug.
 */
export function SetupScreen() {
  const snapshot = useGameStore((s) => s.snapshot);
  const pendingInstall = useGameStore((s) => s.pendingInstall);
  const selectSetupOffer = useGameStore((s) => s.selectSetupOffer);
  const installFacilityMitigation = useGameStore((s) => s.installFacilityMitigation);
  const leaveSetup = useGameStore((s) => s.leaveSetup);
  const setPrecursor = useGameStore((s) => s.setPrecursor);

  // Play with a `liveBuild` rule keeps the shelf open (spec 0067). Mounted from
  // here because this is the one component the composition root already places.
  if (snapshot.phase === "play" && snapshot.liveBuild !== null) return <LiveBuildTray />;
  if (snapshot.phase !== "setup") return null;

  const remaining = snapshot.setupBudgetCredits - snapshot.setupSpentCredits;
  const armed = pendingInstall ? (CARDS[pendingInstall.cardId]?.name ?? pendingInstall.cardId) : null;

  // What the board is waiting for. The panel is off to the side now, so this
  // line is the only place the next click can be explained.
  let hint = "Pick a piece, then click the board to place it. Click something you placed to sell it back.";
  if (pendingInstall && armed) {
    hint = !CARDS[pendingInstall.cardId]?.deploysPipe
      ? `Click a tile to set down the ${armed}.`
      : pendingInstall.fromNodeId === null
        ? `Click the machine the ${armed} runs from.`
        : `Now click the machine it runs to.`;
  }

  return (
    <div className="setup">
      <div className="setup__panel">
        <header className="setup__head">
          <span className="setup__eyebrow">workshop setup</span>
          <h1 className="setup__title">Build the rig</h1>
          <div className="setup__credits">
            <span className="setup__credits-value">{remaining}</span>
            <span className="setup__credits-label">of {snapshot.setupBudgetCredits} ₵ left</span>
          </div>
        </header>

        <p className={`setup__hint${pendingInstall ? " is-armed" : ""}`}>{hint}</p>

        <section className="setup__section">
          <h2>On offer</h2>
          {snapshot.setupCatalogue.length === 0 ? (
            <p className="setup__empty">Nothing to buy — the rig is already built.</p>
          ) : (
            <div className="setup__offers">
              {snapshot.setupCatalogue.map((offer) => {
                const card = CARDS[offer.cardId];
                const isFacility = card?.facility !== undefined;
                const isInstalled = isFacility && snapshot.facilityMitigations.some((m) => m.cardId === offer.cardId);
                const selected = pendingInstall?.cardId === offer.cardId;
                // Selected stays clickable even once it is unaffordable, or
                // there would be no way to put it back down.
                const tooDear = offer.costCredits > remaining && !selected;
                const isDisabled = tooDear || isInstalled;
                const boundRecipe = boundCardRecipeLabel(snapshot, offer.cardId);

                return (
                  <button
                    key={offer.cardId}
                    className={`setup__offer${selected ? " is-selected" : ""}${isInstalled ? " is-installed" : ""}`}
                    disabled={isDisabled}
                    onClick={() => {
                      if (isInstalled) return;
                      if (isFacility) {
                        playCue("cardPick");
                        installFacilityMitigation(offer.cardId);
                      } else {
                        playCue(selected ? "uiClick" : "cardPick");
                        selectSetupOffer(selected ? null : offer.cardId);
                      }
                    }}
                  >
                    <span className="setup__offer-name">
                      {card?.name ?? offer.cardId}
                      {boundRecipe !== null && <small> — makes {boundRecipe}</small>}
                    </span>
                    <span className="setup__offer-cost">{isInstalled ? "installed" : offer.costCredits}</span>
                  </button>
                );
              })}
            </div>
          )}
        </section>

        <InstallRecipePicker snapshot={snapshot} />

        {snapshot.nodes.some((n) => n.kind === "inputTank" && n.sourcing !== null) && (
          <section className="setup__section">
            <h2>Sourcing</h2>
            <div className="setup__sourcing">
              {snapshot.nodes
                .filter((n) => n.kind === "inputTank" && n.sourcing !== null)
                .map((node) => {
                  const sourcing = node.sourcing!; // Filter above narrows it
                  const budgetCost = sourcing.options.budget.costCredits;
                  const premiumCost = sourcing.options.premium.costCredits;
                  // Current cost: 0 if unchosen, otherwise the cost of the chosen tier
                  const currentCost = sourcing.tier === null ? 0 : sourcing.options[sourcing.tier].costCredits;
                  const budgetDelta = budgetCost - currentCost;
                  const premiumDelta = premiumCost - currentCost;

                  return (
                    <div key={node.id} className="setup__sourcing-tank">
                      <div className="setup__sourcing-tank-name">{node.id}</div>
                      <div className="setup__sourcing-tier-buttons">
                        <button
                          className={`setup__sourcing-tier${sourcing.tier === "budget" ? " is-selected" : ""}`}
                          disabled={budgetDelta > remaining && sourcing.tier !== "budget"}
                          onClick={() => {
                            playCue("cardPick");
                            setPrecursor(node.id, "budget");
                          }}
                        >
                          <span className="setup__sourcing-tier-label">Budget</span>
                          <span className="setup__sourcing-tier-cost">{budgetCost}</span>
                        </button>
                        <button
                          className={`setup__sourcing-tier${sourcing.tier === "premium" ? " is-selected" : ""}`}
                          disabled={premiumDelta > remaining && sourcing.tier !== "premium"}
                          onClick={() => {
                            playCue("cardPick");
                            setPrecursor(node.id, "premium");
                          }}
                        >
                          <span className="setup__sourcing-tier-label">Premium</span>
                          <span className="setup__sourcing-tier-cost">{premiumCost}</span>
                        </button>
                      </div>
                      {node.supplyRemaining !== null && (
                        <div className="setup__sourcing-cap">{node.supplyRemaining} units left before this line runs dry</div>
                      )}
                    </div>
                  );
                })}
            </div>
          </section>
        )}

        <footer className="setup__foot">
          <button
            className="setup__go"
            onClick={() => {
              playCue("switchThrow");
              leaveSetup();
            }}
          >
            begin operations
          </button>
        </footer>
      </div>
    </div>
  );
}
